import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { SpecraButton, SpecraInlineBanner } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { EntityFormDialog } from '../Delivery/_shared/components/EntityFormDialog';
import { AttentionQueue } from '../Delivery/CommandCenter/AttentionQueue';
import { useResolveCorrectiveAction } from '../Delivery/Compliance/hooks';
import { MY_WORK_KEY } from './hooks';
import { hiddenWords, WORK_TABS } from './myWork';
function rowOf(it, primary, onResolve) {
    const resolve = it.resolve;
    return {
        id: it.key,
        kind: it.kind,
        badge: it.badge,
        what: it.what,
        where: it.where,
        when: it.when,
        primary,
        verb: resolve ? {
            label: 'Resolve',
            onClick: ()=>onResolve(resolve),
            testId: Testids.dashboard.queueResolve
        } : it.verb
    };
}
export function WaitingOnYou({ work }) {
    const [tab, setTab] = useState('all');
    const [resolving, setResolving] = useState(null);
    const resolveCorrectiveAction = useResolveCorrectiveAction();
    const qc = useQueryClient();
    const count = (t)=>t === 'all' ? work.ranked.length : work.ranked.filter((it)=>it.tab === t).length;
    const tabs = WORK_TABS.filter((t)=>t.value === 'all' || count(t.value) > 0);
    const current = tabs.some((t)=>t.value === tab) ? tab : 'all';
    const inTab = work.ranked.filter((it)=>current === 'all' || it.tab === current);
    const first = work.ranked[0]?.key;
    const notRead = work.total - work.ranked.length;
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            work.failed.length > 0 ? /*#__PURE__*/ _jsxs(SpecraInlineBanner, {
                action: /*#__PURE__*/ _jsx(SpecraButton, {
                    onClick: work.retry,
                    size: "sm",
                    variant: "secondary",
                    children: "Retry"
                }),
                "data-testid": Testids.dashboard.queuePartial,
                tone: "danger",
                variant: "framed",
                children: [
                    work.failed.join(', '),
                    " could not be read; the rest is complete."
                ]
            }) : null,
            /*#__PURE__*/ _jsx(AttentionQueue, {
                empty: work.ranked.length === 0 ? {
                    title: 'Nothing is waiting on you',
                    description: 'Late and at-risk deliverables you own, approvals and requests on programs you lead, corrections assigned to you and items shared with you appear here.'
                } : null,
                footer: notRead > 0 && current === 'all' ? /*#__PURE__*/ _jsxs("span", {
                    className: "text-[length:var(--fs-ui-meta)] text-muted-foreground",
                    children: [
                        notRead.toLocaleString(),
                        " more not listed here; the Command Center has every deliverable."
                    ]
                }) : null,
                hiddenNote: (hidden)=>hiddenWords(hidden.map((h)=>({
                            kind: h.kind
                        }))),
                loading: work.isLoading && work.ranked.length === 0,
                onTabChange: setTab,
                rows: inTab.map((it)=>rowOf(it, it.key === first, setResolving)),
                rule: "Overdue first, then what costs most if it waits",
                tab: current,
                tabs: tabs.map((t)=>({
                        value: t.value,
                        label: t.label,
                        count: count(t.value)
                    })),
                testids: {
                    root: Testids.dashboard.queue,
                    tab: Testids.dashboard.queueTab,
                    list: Testids.dashboard.queueList,
                    row: Testids.dashboard.queueRow,
                    verb: Testids.dashboard.queueVerb,
                    more: Testids.dashboard.queueMore,
                    empty: Testids.dashboard.queueEmpty
                },
                title: "Waiting on You"
            }),
            /*#__PURE__*/ _jsx(EntityFormDialog, {
                defaultValues: {
                    note: ''
                },
                fields: [
                    {
                        kind: 'textarea',
                        name: 'note',
                        label: 'Resolution note',
                        rows: 3
                    }
                ],
                isSubmitting: resolveCorrectiveAction.isPending,
                onOpenChange: (open)=>open ? undefined : setResolving(null),
                onSubmit: async (v)=>{
                    if (resolving) {
                        await resolveCorrectiveAction.mutateAsync({
                            correctiveActionId: resolving.id,
                            resolutionNote: v.note
                        });
                        // The resolved correction leaves Waiting on You.
                        await qc.invalidateQueries({
                            queryKey: [
                                MY_WORK_KEY
                            ]
                        });
                    }
                    setResolving(null);
                },
                open: resolving !== null,
                submitLabel: "Mark Resolved",
                title: "Resolve Correction"
            })
        ]
    });
}
